import { useAnalytics } from "@codat/analytics";
import { Button as OrchardButton } from "@codat/orchard-ui";
import {
  ButtonColor,
  ButtonVariant,
  ButtonProps as OrchardButtonProps,
} from "@codat/orchard-ui/dist/es/components/Button";
import classNames from "classnames";
import React, { forwardRef } from "react";

import styles from "./NavButton.module.css";

interface NavButtonProps extends Omit<OrchardButtonProps, "ref"> {
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement & HTMLAnchorElement>
  ) => void;
  variant?: ButtonVariant;
  className?: string;
  color?: ButtonColor;
  "data-testid": string;
  isDropdownButton?: boolean;
  eventProperties?: Record<string, string>;
  isActive?: boolean;
}

export const NavButton = forwardRef(function NavButton(
  props: NavButtonProps,
  ref: React.ForwardedRef<HTMLButtonElement | HTMLAnchorElement>
) {
  const {
    eventProperties,
    className,
    size = "small",
    color,
    variant,
    "data-testid": testId,
    onClick,
    isActive = false,
    isDropdownButton = false,
    ...buttonProps
  } = props;
  const { sendEvent } = useAnalytics();

  const handleOnClickWithTracking = (
    e: React.MouseEvent<HTMLButtonElement & HTMLAnchorElement>
  ) => {
    sendEvent(
      `${isDropdownButton ? "Dropdown" : "Item"} - Click`,
      eventProperties
    );
    if (onClick) {
      onClick(e);
    }
  };

  const getButtonColor = () => color || (isActive ? "action" : "interface");
  const getButtonVariant = () =>
    variant || (isActive ? "secondary" : "tertiary");

  const buttonColor = getButtonColor();
  const buttonVariant = getButtonVariant();

  return (
    <OrchardButton
      {...buttonProps}
      className={classNames(
        className,
        styles.navButton,
        styles[buttonVariant],
        styles.navButtonHover,
        {
          [styles.activeButton]: isActive,
        }
      )}
      onClick={handleOnClickWithTracking}
      variant={buttonVariant}
      color={buttonColor}
      size={size}
      data-testid={testId}
      {...(isActive ? { "aria-current": "page" } : {})}
      ref={ref}
    />
  );
});
